import { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import EventCopyForm from 'components/forms/EventCopyForm';
import moment from 'moment-timezone';
import useSeasonEventSave from 'hooks/useSeasonEventSave';

export default function EventDuplicator(props) {
    const { eventId, hostId, onCopy, onCancel } = props;

    const { save, loading, confirmationDialog } = useSeasonEventSave();
    const [error, setError] = useState(false);
    const [state, setState] = useState(null);

    const { notify } = useNotification();

    useEffect(() => {
        Promise.all([getEvent(eventId), fetchExpresso(`/apiv2/events/?host=${hostId}`)])
            .then(async ([event, eventsResponse]) => {
                const events = eventsResponse.ok ? await eventsResponse.json() : [];
                setState({
                    defaultValues: {
                        parentMode: Number(event.parent_mode) === 1 ? 1 : 0,
                        parentEventId: null,
                        seatingChartKey: event.seat_chart_key,
                        seatsEnabled: Number(event.seats_flag) === 1,
                        seasonAdmissionCapacity: null,
                        title: event.event_title + ' (copy)',
                        description: event.event_desc,
                        htmlDescription: event.event_desc_html,
                        startDate: moment(event.start_date),
                        endDate: moment(event.end_date),
                        timezone: event.time_zone,
                    },
                    seasons: events.filter(
                        (candidate) =>
                            Number(candidate.parent_mode) === 1 &&
                            !moment(candidate.end_date)
                                .tz(candidate.time_zone, true)
                                .isBefore(moment()),
                    ),
                });
            })
            .catch(() => {
                setError(true);
            });
    }, [eventId, hostId]);

    const handleCopy = (formData) => {
        save({
            endpoint: '/apiv2/events/copy_event',
            method: 'POST',
            body: { ...formData, hostId, sourceId: eventId },
            fallback: 'There was a problem copying your event',
            onSuccess: ({ copyResults }) => {
                const entity = state.defaultValues.parentMode ? 'season' : 'event';
                notify.success(
                    copyResults?.products === false
                        ? `Copied ${entity}, but failed to copy your tickets`
                        : `Copied ${entity} and tickets`,
                );
                onCopy();
            },
        });
    };

    if (error) return <Box p={4}>There was a problem loading your event.</Box>;

    // Waiting for default values to load
    if (state === null) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            {confirmationDialog}
            <EventCopyForm
                defaultValues={state.defaultValues}
                seasons={state.seasons}
                loading={loading}
                onSubmit={handleCopy}
                onCancel={onCancel}
            />
        </>
    );
}

async function getEvent(eventId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}`);
    if (!response.ok) throw new Error('Unable to load event');
    return response.json();
}
